import React from 'react';
import { COLORS } from '../../constants/theme.js';
import { ERROR_MESSAGES, BUTTON_TEXT } from '../../constants/messages.js';

/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // You could also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    // Reset the error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Optionally reload the page or navigate home
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: COLORS.dark,
            color: COLORS.white,
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              maxWidth: '600px',
              width: '100%',
            }}
          >
            {/* Error Icon */}
            <div
              style={{
                fontSize: '4rem',
                marginBottom: '1rem',
                color: COLORS.error,
              }}
            >
              ⚠️
            </div>

            {/* Error Title */}
            <h1
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                marginBottom: '1rem',
                color: COLORS.white,
              }}
            >
              Oops! Something went wrong
            </h1>

            {/* Error Message */}
            <p
              style={{
                fontSize: '1.125rem',
                color: COLORS.gray,
                marginBottom: '2rem',
                lineHeight: '1.6',
              }}
            >
              {ERROR_MESSAGES.UNKNOWN_ERROR}
            </p>

            {/* Error Details (Development Mode Only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details
                style={{
                  marginBottom: '2rem',
                  textAlign: 'left',
                  backgroundColor: COLORS.darkGray,
                  padding: '1rem',
                  borderRadius: '8px',
                  border: `1px solid ${COLORS.primary}`,
                }}
              >
                <summary
                  style={{
                    cursor: 'pointer',
                    color: COLORS.primary,
                    fontWeight: 'bold',
                    marginBottom: '0.5rem',
                  }}
                >
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    fontSize: '0.875rem',
                    color: COLORS.lightGray,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    marginTop: '0.5rem',
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo && (
                    <>
                      {'\n\n'}
                      {this.state.errorInfo.componentStack}
                    </>
                  )}
                </pre>
              </details>
            )}

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={this.handleReset}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: COLORS.white,
                  backgroundColor: COLORS.primary,
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                }}
              >
                {BUTTON_TEXT.RETRY}
              </button>

              <button
                onClick={this.handleReload}
                style={{
                  padding: '0.75rem 1.5rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  color: COLORS.white,
                  backgroundColor: COLORS.darkGray,
                  border: `1px solid ${COLORS.gray}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.cardBg;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = COLORS.darkGray;
                }}
              >
                Reload Page
              </button>
            </div>

            {/* Help Text */}
            <p
              style={{
                marginTop: '2rem',
                fontSize: '0.875rem',
                color: COLORS.gray,
              }}
            >
              If the problem persists, please try clearing your browser cache or
              contact support.
            </p>
          </div>
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;
